import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';

const BlockchainStatusIndicator = ({ className = '' }) => {
  const [status, setStatus] = useState('connected');
  const [transactionCount, setTransactionCount] = useState(1247);
  const [lastBlockTime, setLastBlockTime] = useState(new Date());
  const [networkHealth, setNetworkHealth] = useState(98.5);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    // Simulate real-time blockchain updates
    const interval = setInterval(() => {
      setTransactionCount(prev => prev + Math.floor(Math.random() * 3));
      setLastBlockTime(new Date());
      
      // Simulate occasional status changes
      const random = Math.random();
      if (random < 0.05) {
        setStatus('processing');
        setTimeout(() => setStatus('connected'), 2000);
      } else if (random < 0.02) {
        setStatus('error');
        setTimeout(() => setStatus('connected'), 5000);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const getStatusConfig = () => {
    switch (status) {
      case 'connected':
        return {
          color: 'text-success',
          bgColor: 'bg-success/10',
          icon: 'CheckCircle',
          label: 'Connected',
          description: 'Blockchain network operational'
        };
      case 'processing':
        return {
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          icon: 'Clock',
          label: 'Processing',
          description: 'Transaction verification in progress'
        };
      case 'error':
        return {
          color: 'text-error',
          bgColor: 'bg-error/10',
          icon: 'AlertCircle',
          label: 'Error',
          description: 'Network connectivity issues detected'
        };
      default:
        return {
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          icon: 'Circle',
          label: 'Unknown',
          description: 'Status unavailable'
        };
    }
  };

  const statusConfig = getStatusConfig();

  const formatTime = (date) => {
    return date?.toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className={`relative ${className}`}>
      {/* Compact Status Indicator */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`flex items-center space-x-2 px-3 py-1.5 rounded-full transition-all duration-150 ${statusConfig?.bgColor} hover:opacity-80`}
      >
        <div className="relative">
          <Icon 
            name={statusConfig?.icon} 
            size={12} 
            className={`${statusConfig?.color}`}
          />
          {status === 'connected' && (
            <div className={`absolute inset-0 ${statusConfig?.color} animate-ping opacity-75`}>
              <Icon name={statusConfig?.icon} size={12} />
            </div>
          )}
        </div>
        <span className={`text-xs font-mono ${statusConfig?.color} font-medium`}>
          {statusConfig?.label}
        </span>
        <Icon 
          name="ChevronDown" 
          size={12} 
          className={`${statusConfig?.color} transition-transform duration-150 ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>
      {/* Expanded Status Panel */}
      {isExpanded && (
        <div className="absolute top-full right-0 mt-2 w-80 bg-popover border border-border rounded-lg shadow-modal animate-slide-down z-50">
          <div className="p-4 border-b border-border">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-full ${statusConfig?.bgColor}`}>
                <Icon name={statusConfig?.icon} size={16} className={statusConfig?.color} />
              </div>
              <div>
                <h3 className="font-medium text-popover-foreground">Blockchain Network</h3>
                <p className="text-sm text-muted-foreground">{statusConfig?.description}</p>
              </div>
            </div>
          </div>

          <div className="p-4 space-y-4">
            {/* Network Metrics */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Transactions</p>
                <p className="text-lg font-mono font-semibold text-foreground">
                  {transactionCount?.toLocaleString()}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Network Health</p>
                <p className="text-lg font-mono font-semibold text-foreground">
                  {networkHealth}%
                </p>
              </div>
            </div>

            {/* Last Block Info */}
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Last Block</p>
              <div className="flex items-center justify-between">
                <span className="text-sm font-mono text-foreground">
                  {formatTime(lastBlockTime)}
                </span>
                <span className="text-xs text-muted-foreground">
                  {Math.floor((new Date() - lastBlockTime) / 1000)}s ago
                </span>
              </div>
            </div>

            {/* Hash Display */}
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Latest Hash</p>
              <div className="bg-muted rounded-md p-2">
                <p className="text-xs font-mono text-foreground break-all">
                  0x7f9fade1c0d57a7af66ab4ead79fade1c0d57a7af66ab4ead7c2c2eb7b11a91385
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-2 pt-2">
              <button className="flex-1 px-3 py-2 text-xs font-medium text-primary hover:bg-primary/5 border border-primary rounded-md transition-colors duration-150">
                View Details
              </button>
              <button className="flex-1 px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-muted border border-border rounded-md transition-colors duration-150">
                Refresh
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlockchainStatusIndicator;